import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTiposIngreso, deleteTipoIngreso } from '../actions/tipoIngresoActions';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import TipoIngresoForm from './TipoIngresoForm';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Pagination, Table, Button } from 'react-bootstrap';
import CustomPrintModal from './CustomPrintModal';

const TipoIngresoList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    useEffect(() => {
        const fromHeader = location.state?.fromHeader;
        if (!fromHeader) {
            navigate('/');
        }
    }, [navigate]);

    const dispatch = useDispatch();
    const { tiposIngreso, error } = useSelector(state => state.tipoIngreso);
    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [content, setContent] = useState(null);

    useEffect(() => {
        dispatch(fetchTiposIngreso());
    }, [dispatch]);

    const handleDelete = (id) => {
        confirmAlert({
            title: 'Confirmar eliminación',
            message: '¿Estás seguro de que deseas eliminar este tipo de ingreso?',
            buttons: [
                {
                    label: 'Sí',
                    onClick: () => {
                        dispatch(deleteTipoIngreso(id))
                            .then(() => {
                                toast.success('Tipo de ingreso eliminado correctamente');
                                dispatch(fetchTiposIngreso());
                            })
                            .catch(error => {
                                toast.error(`Error al eliminar: ${error.message}`);
                            });
                    }
                },
                {
                    label: 'No'
                }
            ]
        });
    };

    const handleShowForm = (id = null) => {
        setSelectedId(id);
        setIsEdit(!!id);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setSelectedId(null);
        setIsEdit(false);
    };

    const handlePrintModalShow = () => {
        const content = {
            head: [["N°", "Código", "Nombre", "Monto Referencial"]],
            body: tiposIngreso.map((tipo, index) => [
                index + 1,
                tipo.codigo,
                tipo.nombre,
                `$${(Number(tipo.montoref) || 0).toFixed(2)}`
            ])
        };
        setContent(content);
        setShowPrintModal(true);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentTipos = tiposIngreso.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(tiposIngreso.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    if (error) {
        toast.error(`Error al cargar los datos: ${error}`);
    }

    return (
        <div className="list1-container container-list">
            <h2>Listado de Tipos de Ingreso</h2>
            <div className="row mt-3">
                <div className='col-lg-8'>
                    <label htmlFor="items-per-page">Registros por página:</label>
                    <select 
                        id="items-per-page" 
                        value={itemsPerPage} 
                        onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                    >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="30">30</option>
                    </select>
                </div>
                <div className='col-lg-3 float-lg-end'>
                    <Button 
                        variant="accent" 
                        onClick={() => handleShowForm()}
                    >
                        <i className="bi bi-plus-lg"></i> Nuevo Tipo
                    </Button>
                </div>
            </div>

            <div className='table-container'>
                <Table hover striped>
                    <thead>
                        <tr>
                            <th>N°</th>
                            <th>Código</th>
                            <th>Nombre</th>
                            <th>Monto Referencial</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentTipos.map((tipo, index) => (
                            <tr key={tipo.id}>
                                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                <td>{tipo.codigo}</td>
                                <td>{tipo.nombre}</td>
                                <td>${(Number(tipo.montoref) || 0).toFixed(2)}</td>
                                <td>
                                    <Button 
                                        variant="outline-accent" 
                                        onClick={() => handleShowForm(tipo.id)}
                                    >
                                        <i className="bi bi-pencil"></i>
                                    </Button>
                                    <Button 
                                        variant="outline-danger" 
                                        onClick={() => handleDelete(tipo.id)}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <div className='button-group separate'>
                <Button variant="secondary" onClick={() => navigate('/')}>
                    <i className="bi bi-arrow-left"></i> Volver
                </Button>
                <Button variant="accent" onClick={handlePrintModalShow}>
                    <i className="bi bi-printer"></i> Imprimir
                </Button>
            </div>

            <Pagination className="mt-3">
                <Pagination.First 
                    onClick={() => paginate(1)} 
                    disabled={currentPage === 1} 
                />
                <Pagination.Prev 
                    onClick={() => paginate(currentPage - 1)} 
                    disabled={currentPage === 1} 
                />
                {[...Array(totalPages)].map((_, idx) => (
                    <Pagination.Item 
                        key={idx + 1} 
                        active={idx + 1 === currentPage}
                        onClick={() => paginate(idx + 1)}
                    >
                        {idx + 1}
                    </Pagination.Item>
                ))}
                <Pagination.Next 
                    onClick={() => paginate(currentPage + 1)} 
                    disabled={currentPage === totalPages} 
                />
                <Pagination.Last 
                    onClick={() => paginate(totalPages)} 
                    disabled={currentPage === totalPages} 
                />
            </Pagination>

            {showForm && (
                <TipoIngresoForm 
                    show={showForm} 
                    handleClose={handleCloseForm} 
                    isEdit={isEdit} 
                    selectedId={selectedId} 
                />
            )}

            <CustomPrintModal 
                show={showPrintModal} 
                handleClose={() => setShowPrintModal(false)} 
                title="LISTADO DE TIPOS DE INGRESO" 
                content={content} 
            />
        </div>
    );
};

export default TipoIngresoList;